import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Printer, Send, Mail, CheckCircle } from "lucide-react";
import { Account } from "@features/cx/outstanding/types";
import { useState } from "react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useSendReminder } from "@features/cx/outstanding/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface SendReminderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account: Account;
  outstandingAmount: number;
  onReminderSent?: (reminderDetails: any) => void;
}

export function SendReminderDialog({
  isOpen,
  onClose,
  account,
  outstandingAmount,
  onReminderSent,
}: SendReminderDialogProps) {
  console.log("account", account);
  const remoteUtilityId = useRemoteUtilityId();
  const [reminderSent, setReminderSent] = useState(false);
  const sendReminder = useSendReminder();

  const formatCurrency = (amount: any) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined | null) => {
    // Handle null, undefined, or empty strings
    if (!dateString || dateString.trim() === "") {
      return "N/A";
    }

    try {
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date string: ${dateString}`);
        return "Invalid Date";
      }

      return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch (error) {
      console.error(`Error formatting date: ${dateString}`, error);
      return "Invalid Date";
    }
  };

  const formFields: FormField[] = [
    {
      name: "reminderType",
      type: "select",
      label: "Reminder Type",
      required: true,
      className: "col-span-1",
      options: [
        { value: "First Reminder", label: "First Reminder" },
        { value: "Second Reminder", label: "Second Reminder" },
        { value: "Final Reminder", label: "Final Reminder" },
      ],
    },
    {
      name: "additionalMessage",
      type: "textarea",
      label: "Additional Message (Optional)",
      placeholder: "Enter any additional information for the customer...",
      rows: 3,
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      reminderType: "First Reminder",
      additionalMessage: "",
    },
  });

  const getReminderTitle = (type: string) => {
    switch (type) {
      case "First Reminder":
        return "First Payment Reminder";
      case "Second Reminder":
        return "Second Payment Reminder";
      case "Final Reminder":
        return "Final Payment Reminder";
      default:
        return "Payment Reminder";
    }
  };

  const getReminderMessage = (type: string, additionalMessage: string) => {
    const baseMessage = `Dear ${
      account.firstName + " " + account.lastName
    },\n\nThis is a ${type} reminder that your water and sewer account has an outstanding balance of ${formatCurrency(
      account?.billData?.outstandingBalance
    )}.\n\nAccount Number: ${account.consumerNo}\nService Address: ${account.addressData?.area}, ${account.addressData?.subArea}, ${account.addressData?.premise}
    \nDays Outstanding: ${
      account?.billData?.daysSinceLastBill
    } days\n\nPlease contact our office to make payment arrangements or pay online at our customer portal.\n\nThank you for your prompt attention to this matter.`;
    return additionalMessage
      ? `${baseMessage}\n\nAdditional Information:\n${additionalMessage}`
      : baseMessage;
  };

  const handleSendReminder = () => {
    const formValues = form.getValues();
    const reminderDetails = {
      remote_utility_id: remoteUtilityId,
      channel_type: "EMAIL",
      to: [account.email],
      body: formValues.additionalMessage,
      context: {
        entity_id: account.id,
        action_type: "disconnection_reminder",
        reminder_type: formValues.reminderType,
      },
    };
    sendReminder.mutateAsync(reminderDetails);
    form.reset();
    onClose();
  };

  const handlePrintReminder = () => {
    const formValues = form.getValues();
    const reminderType = formValues.reminderType;
    const additionalMessage = formValues.additionalMessage;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${getReminderTitle(reminderType)} - ${
        account.accountNumber
      }</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              .header { text-align: center; border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
              .section { margin-bottom: 25px; }
              .notice-box { border: 2px solid #2563eb; padding: 20px; margin: 20px 0; background-color: #eff6ff; }
              .footer { margin-top: 40px; font-size: 14px; color: #666; }
              @media print { body { margin: 20px; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${getReminderTitle(reminderType)}</h1>
           <p>
  ${account.consumerMappingData
    ?.map((item) => item.utilityService)
    .reduce((acc, curr, index, arr) => {
      if (index === 0) return curr;
      if (index === arr.length - 1) return `${acc} & ${curr}`;
      return `${acc}, ${curr}`;
    }, "")}
</p>
              <p>Date: ${formatDate(new Date().toISOString())}</p>
            </div>
            
            <div class="notice-box">
              <h2>Outstanding Balance Notice</h2>
              <p><strong>Amount Due:</strong> ${formatCurrency(
                account?.billData?.outstandingBalance
              )}</p>
              <p><strong>Days Outstanding:</strong> ${
                account?.billData?.daysSinceLastBill
              } days</p>
            </div>

            <div class="section">
              <h2>Account Information</h2>
              <p><strong>Account Number:</strong> ${account.consumerNo}</p>
              <p><strong>Customer Name:</strong> ${
                account.firstName + " " + account.lastName
              }</p>
              <p><strong>Service Address:</strong> ${account.addressData?.area}, ${account.addressData?.subArea}, ${account.addressData?.premise}</p>
              <p><strong>Customer Category:</strong> ${account.categoryDisplay}</p>
            </div>

            <div class="section">
              <h2>Message</h2>
              <p style="white-space: pre-line;">${getReminderMessage(
                reminderType,
                additionalMessage
              )}</p>
            </div>

            <div class="section">
              <h2>Payment Options</h2>
              <ul>
                <li>Pay online at our customer portal</li>
                <li>Visit our office during business hours</li>
              </ul>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center text-blue-600">
            <Mail className="mr-2 h-5 w-5" />
            Send Payment Reminder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-blue-800 mb-2">
                PAYMENT REMINDER
              </h3>
              <p className="text-sm text-blue-700">
                This reminder will be sent to the customer to notify them of
                their outstanding balance.
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Account Details</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account:</span>
                    <span>{account.consumerNo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer:</span>
                    <span>{account.firstName + " " + account.lastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category:</span>
                    <span>{account.categoryDisplay}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Outstanding Balance</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount Due:</span>
                    <span className="font-bold text-red-600">
                      {formatCurrency(account?.billData?.outstandingBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Days Outstanding:
                    </span>
                    <span>{account.billData.daysSinceLastBill} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Payment:</span>
                    <span>{formatDate(account.billData.lastPaymentDate)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSendReminder}
              onReset={onClose}
              gridColumns={1}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                spacing: "comfortable",
                classes: {
                  form: "space-y-6",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  container: "space-y-1",
                },
              }}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSendReminder} className="flex-1">
              <Send className="mr-2 h-4 w-4" />
              Send Reminder
            </Button>
            <Button variant="outline" onClick={handlePrintReminder}>
              <Printer className="mr-2 h-4 w-4" />
              Preview & Print
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
